/**
 * Watrly Sidebar Component
 * ========================
 * مكون القائمة الجانبية
 */

import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import './Sidebar.css';

const Sidebar = ({
  items = [],
  activeItem,
  onItemClick,
  logo,
  collapsed = false,
  onCollapse,
  footer,
  className = '',
  ...props
}) => {
  const { isRTL, t } = useTheme();

  const classes = [
    'watrly-sidebar',
    collapsed && 'watrly-sidebar--collapsed',
    className,
  ].filter(Boolean).join(' ');

  return (
    <aside className={classes} dir={isRTL ? 'rtl' : 'ltr'} {...props}>
      {/* Logo */}
      <div className="watrly-sidebar__header">
        {logo ? (
          <div className="watrly-sidebar__logo">{logo}</div>
        ) : (
          <div className="watrly-sidebar__logo">
            <svg viewBox="0 0 24 24" className="watrly-sidebar__logo-icon">
              <path
                d="M12 2C12 2 6 10 6 14C6 17.3137 8.68629 20 12 20C15.3137 20 18 17.3137 18 14C18 10 12 2 12 2Z"
                fill="#0ABAB5"
              />
            </svg>
            {!collapsed && <span className="watrly-sidebar__logo-text">وترلي</span>}
          </div>
        )}

        {onCollapse && (
          <button
            className="watrly-sidebar__collapse-btn"
            onClick={() => onCollapse(!collapsed)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points={collapsed ? (isRTL ? '15 18 9 12 15 6' : '9 18 15 12 9 6') : (isRTL ? '9 18 15 12 9 6' : '15 18 9 12 15 6')} />
            </svg>
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="watrly-sidebar__nav">
        <ul className="watrly-sidebar__list">
          {items.map((item, index) => (
            <SidebarItem
              key={item.id || index}
              item={item}
              isActive={activeItem === item.id}
              collapsed={collapsed}
              onClick={() => onItemClick?.(item)}
            />
          ))}
        </ul>
      </nav>

      {/* Footer */}
      {footer && <div className="watrly-sidebar__footer">{footer}</div>}
    </aside>
  );
};

// عنصر القائمة
const SidebarItem = ({ item, isActive, collapsed, onClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;

  const handleClick = () => {
    if (hasChildren) {
      setIsOpen(!isOpen);
    } else {
      onClick?.();
    }
  };

  const classes = [
    'watrly-sidebar__item',
    isActive && 'watrly-sidebar__item--active',
    item.disabled && 'watrly-sidebar__item--disabled',
  ].filter(Boolean).join(' ');

  return (
    <li className={classes}>
      <button
        className="watrly-sidebar__link"
        onClick={handleClick}
        disabled={item.disabled}
        title={collapsed ? item.label : undefined}
      >
        {item.icon && <span className="watrly-sidebar__icon">{item.icon}</span>}
        {!collapsed && (
          <>
            <span className="watrly-sidebar__label">{item.label}</span>
            {item.badge && (
              <span className={`watrly-sidebar__badge watrly-sidebar__badge--${item.badge.type || 'default'}`}>
                {item.badge.value}
              </span>
            )}
            {hasChildren && (
              <svg
                className={`watrly-sidebar__arrow ${isOpen ? 'watrly-sidebar__arrow--open' : ''}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            )}
          </>
        )}
      </button>

      {hasChildren && isOpen && !collapsed && (
        <ul className="watrly-sidebar__submenu">
          {item.children.map((child, index) => (
            <li key={child.id || index} className="watrly-sidebar__subitem">
              <button
                className={`watrly-sidebar__sublink ${child.active ? 'watrly-sidebar__sublink--active' : ''}`}
                onClick={() => onClick?.(child)}
                disabled={child.disabled}
              >
                {child.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

// زر تسجيل الخروج
export const LogoutButton = ({ onClick, label, collapsed = false }) => {
  const { t } = useTheme();

  return (
    <button
      className="watrly-sidebar__logout"
      onClick={onClick}
      title={collapsed ? (label || t?.('auth.logout') || 'تسجيل الخروج') : undefined}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <polyline points="16 17 21 12 16 7" />
        <line x1="21" y1="12" x2="9" y2="12" />
      </svg>
      {!collapsed && <span>{label || t?.('auth.logout') || 'تسجيل الخروج'}</span>}
    </button>
  );
};

export default Sidebar;
